import { Suspense, lazy } from 'react';
import { Link } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import {
  ArrowRight, Calculator, Coins, ListChecks, Dog, Plane, ShieldCheck,
  Banknote, Home as HomeIcon, Languages, Hotel, MapPin,
} from 'lucide-react';

import Container from '@/components/layout/Container';
import TrustBand from '@/components/layout/TrustBand';
import SEOHead from '@/components/seo/SEOHead';

import ArticleCard from '@/components/article/ArticleCard';
import ExpandableArticleCard from '@/components/article/ExpandableArticleCard';
import HeroSearchBar from '@/components/nav/HeroSearchBar';
import ContinueReading from '@/components/widgets/ContinueReading';
import SpanishPhraseOfDay from '@/components/widgets/SpanishPhraseOfDay';
// Sprint 4 polish : tampon éditorial "Issue 01 · 2026" qui remplace le
// label mono discret du Sprint 1 par un médaillon visuel rotaté.
import EditorialStamp from '@/components/article/EditorialStamp';
// Lazy : composants hors chemin critique → sortis du bundle eager (budget index).
//  · HomeSectionTOC : sommaire latéral sticky, desktop ≥1280px uniquement.
//  · LogoIntro : révélation logo jouée au scroll, sous le hero plein écran.
const HomeSectionTOC = lazy(() => import('@/components/nav/HomeSectionTOC'));
const LogoIntro = lazy(() => import('@/components/brand/LogoIntro'));
const ExpatPersonaQuiz = lazy(() => import('@/components/quiz/ExpatPersonaQuiz'));
const PanamaPlacesGallery = lazy(() => import('@/components/widgets/PanamaPlacesGallery'));
import { categories } from '@/data/categories';
import NewsletterBanner from '@/components/newsletter/NewsletterBanner';
import LoadingSpinner from '@/components/feedback/LoadingSpinner';
import { BudgetCalculator } from '@/components/lazy';
import BgVideo from '@/components/media/BgVideo';
import { RevealOnScroll, TextReveal, MagneticButton } from '@/components/effects/MotionPrimitives';
import Tilt from '@/components/effects/Tilt';

import { SUPPORTED_LANGS, DEFAULT_LANG } from '@/i18n';
import WaveMark from '@/components/brand/WaveMark';
import { pathFor } from '@/data/routes';
import { getPillarArticles, getLatestArticles, allArticles } from '@/utils/articles';
import { VISIBLE_PRODUCTS } from '@/data/products';

// ============================================================
// Home — refonte v3 (2026-05-25, Audit design Sprint 1)
//
// Passe de 18 sections → 9, selon AUDIT_DESIGN.md.
//
// SUPPRIMÉ :
//   - SocialProofBanner       → déplacé en footer (mode compact, sans animation)
//   - InstantAnswersWidget    → à migrer vers /faq (prématuré en home)
//   - WeatherWidget           → migré vers /vivre-au-panama
//   - TestimonialsRotator     → migré vers /a-propos (doublon avec testimonials statiques)
//   - PhotoStripe (Unsplash)  → contradictoire avec promesse "vérifié terrain"
//   - Static testimonials grid → doublon avec page /a-propos
//   - FunFactRotator          → migré vers sidebar article
//   - SpanishPhraseOfDay home → RÉ-AJOUTÉ 2026-06-24 (section 3b) suite au
//     retour feedback « mettre plus haut » ; reste aussi en footer compact.
//   - StreakBadge sidecar     → gamification SaaS hors registre éditorial
//
// CORRIGÉ :
//   - TOOL_TILES : 4 routes dédiées (toolBudget/Converter/Visa/Dog —
//     audit C·03+ Sprint 3) au lieu des ancres temporaires de Sprint 1.
//   - ShimmerButton → btn-primary plein (évite l'effet "Black Friday").
//   - Browse-by-topic : grid-cols-2 mobile (lisibilité pictos 360px).
//   - InstantAnswers retiré : la home ne demande plus de "travail" au visiteur.
//   - RevealOnScroll : conservé uniquement sur 3 sections phares.
//   - FlagBand ajouté en transition après les vidéos Jade : seul endroit
//     du site qui utilise les VRAIES couleurs du drapeau (rouge/bleu/blanc).
// ============================================================

const TOOL_TILES = [
  { icon: Calculator, key: 'budget', route: 'toolBudget' },
  { icon: Coins, key: 'converter', route: 'toolConverter' },
  { icon: ListChecks, key: 'visa', route: 'toolVisa' },
  { icon: Dog, key: 'dog', route: 'toolDog' },
];

const CATEGORY_ICONS = {
  'visa-residence': ShieldCheck,
  'banque-argent': Banknote,
  'immobilier': HomeIcon,
  'vols': Plane,
  'animaux-chiens': Dog,
  'assurance': Hotel,
  'espagnol': Languages,
  'vie-quotidienne': MapPin,
};

// T1 (value-ladder strategy, brief zone 5) — teaser des 4 paliers boutique.
// Renvoie vers /shop. Les libellés de palier réutilisent pages:shop.tier.*
// (trilingues, posés en T2). Le « fini » monte avec le palier (accent + sceau).
const HOME_TIERS = [
  { key: 'discovery', band: '$5–19', eyebrow: 'text-navy-700 dark:text-white/70', ring: 'border-navy-900/10', seal: false,
    blurb: { fr: 'Premiers repères : les guides visa & chien essentiels.', en: 'First landmarks: the essential visa & dog guides.', es: 'Primeros puntos: las guías esenciales de visa y perro.' } },
  { key: 'further', band: '$20–39', eyebrow: 'text-navy-700 dark:text-white/70', ring: 'border-navy-900/10', seal: false,
    blurb: { fr: 'Tu avances : le Toolkit complet et les bundles.', en: 'Going further: the full Toolkit and bundles.', es: 'Avanzas: el Toolkit completo y los bundles.' } },
  { key: 'pro', band: '$40–99', eyebrow: 'text-bordeaux-700 dark:text-bordeaux-400', ring: 'border-bordeaux-600/20', seal: false,
    blurb: { fr: "Tu passes à l'action : conseil 1-à-1 et audit personnalisé.", en: 'Taking action: 1-to-1 consulting and a personal audit.', es: 'Pasas a la acción: consultoría 1-a-1 y auditoría personal.' } },
  { key: 'premium', band: '$100+', eyebrow: 'text-gold-700 dark:text-gold-400', ring: 'border-gold-500/30', seal: true,
    blurb: { fr: 'Accompagnement complet : pack 3 questions, suivi mensuel.', en: 'Full support: 3-question pack, monthly follow-up.', es: 'Acompañamiento total: pack 3 preguntas, seguimiento mensual.' } },
];

// ============================================================
// ROUTER « Où en es-tu ? » — 3 stades de décision (maquette validée 2026-06).
// Stratégie « site progressif » : une porte d'entrée par étape, du rêveur au
// futur installé. Chaque carte mène à la destination la plus utile à ce stade
// (découvre→blog, compare→comparatifs, installe→boutique). Promu juste après
// le hero pour que CHAQUE visiteur se segmente immédiatement.
// ============================================================
const STAGES = [
  {
    key: 'dream',
    route: 'blog',
    // Rouille-dark et pas rouille : papier os sur #BE5C3C ne donne que
    // 3,76:1, sous le seuil AA de 4,5. Sur #9C4628 on monte à 5,42:1.
    card: 'bg-bordeaux-700 text-cream-50',
    kicker: { fr: 'Je rêve', en: 'I dream', es: 'Sueño' },
    title: { fr: 'Je découvre', en: 'I discover', es: 'Descubro' },
    body: {
      fr: "Le Panama me fait envie, mais je n'en suis pas encore là. Donne-moi des raisons d'y croire.",
      en: "Panama appeals to me, but I'm not there yet. Give me reasons to believe.",
      es: 'Panamá me atrae, pero aún no estoy ahí. Dame razones para creer.',
    },
    cta: { fr: 'Le rêve, en images', en: 'The dream, in pictures', es: 'El sueño, en imágenes' },
  },
  {
    key: 'compare',
    route: 'comparisons',
    // Encre sur océan ne donnait que 2,84:1 — la carte la plus illisible des
    // trois, et la raison du « le texte ne ressort pas ». Papier os : 4,90:1.
    card: 'bg-gold-500 text-cream-50',
    kicker: { fr: 'Je me renseigne', en: "I'm researching", es: 'Me informo' },
    title: { fr: 'Je compare', en: 'I compare', es: 'Comparo' },
    body: {
      fr: "C'est sérieux. Je veux les coûts réels, les visas, ce qui marche et ce qui coince.",
      en: "It's serious. I want the real costs, the visas, what works and what doesn't.",
      es: 'Es serio. Quiero los costos reales, las visas, lo que funciona y lo que no.',
    },
    cta: { fr: 'Les guides & comparatifs', en: 'Guides & comparisons', es: 'Guías y comparativas' },
  },
  {
    key: 'settle',
    route: 'shop',
    card: 'bg-navy-900 text-cream-50',
    kicker: { fr: 'Je suis décidé·e', en: "I've decided", es: 'Estoy decidido·a' },
    title: { fr: "Je m'installe", en: 'I settle in', es: 'Me instalo' },
    body: {
      fr: "Je passe à l'action. Je veux le parcours complet, étape par étape, sans rien oublier.",
      en: 'I take action. I want the full path, step by step, nothing forgotten.',
      es: 'Paso a la acción. Quiero el recorrido completo, paso a paso, sin olvidar nada.',
    },
    cta: { fr: 'La boutique & le conseil', en: 'The shop & consulting', es: 'La tienda y la asesoría' },
  },
];

// GLANCE — unités + kickers par langue (étaient FR sur les 3 homes → séparés
// 2026-07-08, demande Jade : « je veux que les langues soient bien séparées »).
const GLANCE = {
  articles: {
    unit: { fr: 'articles', en: 'articles', es: 'artículos' },
    k: { fr: 'Reportages FR · EN · ES', en: 'Reporting FR · EN · ES', es: 'Reportajes FR · EN · ES' },
  },
  personas: {
    unit: { fr: 'personas', en: 'personas', es: 'perfiles' },
    k: {
      fr: 'Retraité · Entrepreneur · Nomade · Famille · Explorateur',
      en: 'Retiree · Entrepreneur · Nomad · Family · Explorer',
      es: 'Jubilado · Emprendedor · Nómada · Familia · Explorador',
    },
  },
  memos: {
    unit: { fr: 'mémos', en: 'memos', es: 'memos' },
    k: { fr: 'Guides PDF vérifiés terrain', en: 'Field-checked PDF collection', es: 'Guías PDF verificadas en terreno' },
  },
  issue: {
    unit: { fr: 'issue 01', en: 'issue 01', es: 'edición 01' },
    k: { fr: 'Édition courante Inside Panama', en: 'Current Inside Panama issue', es: 'Edición actual de Inside Panama' },
  },
};

// PROOF « Pourquoi nous croire » — ancre de confiance qualitative (≠ glance
// chiffrée). 3 piliers, bloc sombre, juste après le router (maquette zone 3).
const PROOFS = [
  {
    key: 'lived',
    title: { fr: 'Vécu, pas recyclé', en: 'Lived, not recycled', es: 'Vivido, no reciclado' },
    body: {
      fr: "Visa Friendly Nations, compte bancaire, import de nos chiens : on documente ce qu'on traverse, en temps réel.",
      en: 'Friendly Nations visa, bank account, importing our dogs: we document what we go through, in real time.',
      es: 'Visa Friendly Nations, cuenta bancaria, importación de nuestros perros: documentamos lo que vivimos, en tiempo real.',
    },
  },
  {
    key: 'transparent',
    title: { fr: 'Transparence totale', en: 'Full transparency', es: 'Transparencia total' },
    body: {
      fr: "Sources citées pour chaque chiffre, liens d'affiliation toujours déclarés, zéro avis acheté.",
      en: 'Sources cited for every figure, affiliate links always disclosed, zero bought reviews.',
      es: 'Fuentes citadas para cada cifra, enlaces de afiliación siempre declarados, cero reseñas compradas.',
    },
  },
  {
    key: 'no-promises',
    title: { fr: 'Pas de promesses', en: 'No false promises', es: 'Sin falsas promesas' },
    body: {
      fr: "On n'est ni avocates ni fiscalistes. On dit ce qu'on sait, et on te renvoie vers un pro quand il le faut.",
      en: "We're neither lawyers nor tax advisors. We say what we know, and point you to a pro when needed.",
      es: 'No somos abogadas ni asesoras fiscales. Decimos lo que sabemos y te remitimos a un profesional cuando hace falta.',
    },
  },
];

export default function Home() {
  const { t, i18n } = useTranslation();
  const reduce = useReducedMotion();

  const lang = SUPPORTED_LANGS.includes(i18n.language) ? i18n.language : DEFAULT_LANG;
  const pillarArticles = getPillarArticles({ lang, limit: 6 });
  const latestArticles = getLatestArticles({ lang, limit: 6 });
  // Count RÉEL du catalogue (publié, 3 langues) — l'ancien « 132 » hardcodé
  // était périmé (215 réels). Dérivé du registre → ne périme plus.
  const totalArticles = allArticles().filter((a) => a.frontmatter.status !== 'draft').length;

  const stagger = reduce
    ? {}
    : {
        animate: 'show',
        initial: 'hidden',
        variants: { show: { transition: { staggerChildren: 0.06, delayChildren: 0.1 } } },
      };
  const item = reduce
    ? { initial: false }
    : {
        variants: {
          hidden: { opacity: 0, y: 12 },
          show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] } },
        },
      };

  return (
    <>
      <SEOHead title={`Inside Panama — ${t('common:tagline')}`} description={t('common:brief')} />
      {/* Sommaire numéroté vertical sticky — desktop ≥1280px uniquement.
          Honore la promesse "Issue 01" du hero (audit I·08).
          Lazy + fallback null : nav hors flux (sticky), aucun CLS. */}
      <Suspense fallback={null}>
        <HomeSectionTOC />
      </Suspense>
      {/* JSON-LD : AUCUN bloc inline ici. GlobalSchema (monté dans Layout, donc
          déjà présent sur la home) émet WebSite + NewsMediaOrganization + les 2
          Person avec @id. L'ancien bloc Organization/WebSite inline dupliquait
          ces entités avec des données DIVERGENTES (foundingDate 2025 vs 2026-03,
          taxID préfixé) → retiré 2026-07-08. Source de vérité = GlobalSchema.jsx. */}

      {/* ============================================================
          1. HERO — skyline aerial video + headline + search + CTA + trust
          ============================================================ */}
      <BgVideo
        src="/videos/panama-skyline.mp4"
        poster="/images/places/cinta-costera/cinta-costera-vue-ocean.jpg"
        className="-mt-20 min-h-[100svh] pt-20"
        overlay="bg-gradient-to-b from-navy-900/55 via-navy-900/35 to-cream-50/90"
        force
        parallax
      >{/* Sprint 7 (audit P·02) — `mobileVideo` retiré : sur <768px le poster JPG (~300KB) suffit, le MP4 4-8MB ne se charge plus. Gain LCP mobile ~800ms. */}
        <section className="relative flex min-h-[100svh] items-center overflow-hidden pt-2">
          {/* Sprint 4 — tampon éditorial "Issue 01 · 2026" en haut à droite
              du hero. Renforce la promesse magazine du Lora italique. */}
          <div
            aria-hidden="true"
            className="pointer-events-none absolute right-6 top-24 hidden lg:right-12 lg:block"
          >
            <EditorialStamp label="Issue" num="01" sub={String(new Date().getFullYear())} />
          </div>

          <div
            aria-hidden="true"
            className="pointer-events-none absolute left-4 top-24 hidden text-[10px] font-mono uppercase tracking-[0.4em] text-cream-50/50 sm:left-6 sm:block lg:left-8"
          >
            ISSUE 01 · {new Date().getFullYear()}
          </div>

          <Container className="relative">
            <motion.div {...stagger} className="max-w-2xl">
              {/* V4 (2026-05-26) : eyebrow magazine avec ligne 28px préfixe.
                  Hérite couleur gold-500 sur fond dark hero (cohérent avec
                  block.dark du guide V4). */}
              <motion.div {...item} className="mb-7">
                <span className="v4-eyebrow text-gold-500">
                  {t('pages:home.eyebrow')}
                </span>
              </motion.div>

              {/* V4 .v4-display : Lora italic 3.4rem→8rem clamp, tracking
                  -0.04em. Sur dark hero on force la couleur cream-50.
                  TextReveal continue d'animer caractère par caractère. */}
              <TextReveal
                as="h1"
                stagger={0.04}
                delay={0.15}
                className="v4-display !text-cream-50 drop-shadow-lg"
                style={{ fontSize: 'clamp(2.6rem, 6vw + 1rem, 5.5rem)' }}
              >
                {t('pages:home.headline')}
              </TextReveal>

              <motion.p
                {...item}
                className="mt-7 max-w-2xl text-pretty font-serif italic text-lg leading-relaxed text-cream-50/95 drop-shadow md:text-xl"
              >
                {t('pages:home.subhead')}
              </motion.p>

              <motion.div {...item} className="mt-8 max-w-xl">
                <HeroSearchBar />
              </motion.div>

              {/* CTA primaire : bouton plein bordeaux (sortie de ShimmerButton
                  qui sentait la promo Black Friday — audit I·06). */}
              <motion.div {...item} className="mt-6 flex flex-wrap items-center gap-3">
                <MagneticButton strength={0.2} radius={90}>
                  <Link to={pathFor('startHere', lang)} className="btn-primary">
                    {t('pages:home.ctaPrimary')}
                    <ArrowRight size={16} aria-hidden="true" className="ml-1.5 inline" />
                  </Link>
                </MagneticButton>
                <Link to={pathFor('blog', lang)} className="btn-secondary-solid">
                  {t('pages:home.ctaSecondary')}
                </Link>
              </motion.div>

              <motion.div {...item} className="mt-10">
                <TrustBand />
              </motion.div>
            </motion.div>
          </Container>
        </section>
      </BgVideo>

      {/* ============================================================
          ROUTER — « Où en es-tu ? » (maquette validée 2026-06)
          Site progressif : 3 portes d'entrée par stade de décision, du
          rêveur au futur installé. Promu juste après le hero pour que
          chaque visiteur se segmente immédiatement (remplace l'ancienne
          section « Par où commencer » enterrée en position 8).
          ============================================================ */}
      <section id="home-router" className="bg-cream-100/70 py-16 md:py-20 dark:bg-white/[0.02]">
        <Container>
          <header className="mx-auto mb-10 max-w-2xl text-center">
            <p className="eyebrow inline-flex items-center gap-3 text-bordeaux-700 dark:text-bordeaux-400">
              <span className="h-px w-9 bg-bordeaux-600/40" />
              {lang === 'en' ? 'Where are you?' : lang === 'es' ? '¿Dónde estás?' : 'Où en es-tu ?'}
              <span className="h-px w-9 bg-bordeaux-600/40" />
            </p>
            <h2 className="editorial-h2-section mt-3 dark:text-white">
              {lang === 'en'
                ? 'Three paths, wherever you stand'
                : lang === 'es'
                ? 'Tres caminos, estés donde estés'
                : 'Trois chemins, selon où tu en es'}
            </h2>
            <p className="mt-3 font-serif text-lg italic leading-relaxed text-navy-700/80 dark:text-white/70">
              {lang === 'en'
                ? 'The same front door for everyone. Each person heads to their own stage — from dreamer to soon-to-be local.'
                : lang === 'es'
                ? 'La misma puerta de entrada para todos. Cada quien avanza hacia su etapa — del soñador al futuro residente.'
                : "La même porte d'entrée pour tous. Chacun repart vers son étape — du rêveur au futur installé."}
            </p>
          </header>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            {STAGES.map((s) => (
              <Tilt key={s.key} className="h-full">
              <Link
                to={pathFor(s.route, lang)}
                className={`group flex h-full min-h-[200px] flex-col rounded-card p-7 shadow-card transition hover:-translate-y-1 hover:shadow-xl ${s.card}`}
              >
                <span className="font-mono text-[11px] uppercase tracking-[0.18em] opacity-80">
                  {s.kicker[lang] || s.kicker.fr}
                </span>
                <h3 className="mt-2 font-serif text-2xl font-bold italic">
                  {s.title[lang] || s.title.fr}
                </h3>
                <p className="mt-2 text-sm leading-relaxed opacity-90">
                  {s.body[lang] || s.body.fr}
                </p>
                <span className="mt-auto inline-flex items-center gap-1.5 pt-5 text-sm font-bold">
                  {s.cta[lang] || s.cta.fr}
                  <ArrowRight size={15} aria-hidden="true" className="transition-transform group-hover:translate-x-0.5" />
                </span>
              </Link>
              </Tilt>
            ))}
          </div>

          <p className="mt-7 text-center text-sm text-navy-700/80 dark:text-white/60">
            {lang === 'en' ? 'Not sure where you fit? ' : lang === 'es' ? '¿No sabes dónde encajas? ' : 'Pas sûr·e de ton étape ? '}
            <Link
              to={pathFor('startHere', lang)}
              className="font-semibold text-bordeaux-700 underline-offset-2 hover:underline dark:text-bordeaux-400"
            >
              {lang === 'en' ? 'Take the 30-second quiz →' : lang === 'es' ? 'Haz el test de 30 segundos →' : 'Fais le quiz de 30 secondes →'}
            </Link>
          </p>
        </Container>
      </section>

      {/* ============================================================
          PROOF — « Pourquoi nous croire » (maquette validée 2026-06)
          Ancre de confiance qualitative (≠ glance chiffrée). Bloc sombre.
          ============================================================ */}
      <RevealOnScroll as="section" className="bg-navy-900 py-16 text-cream-50 md:py-20">
        <Container>
          <header className="mx-auto mb-10 max-w-2xl text-center">
            <p className="eyebrow inline-flex items-center gap-3 text-gold-400">
              <span className="h-px w-9 bg-gold-400/40" />
              {lang === 'en' ? 'Why trust us' : lang === 'es' ? 'Por qué confiar' : 'Pourquoi nous croire'}
              <span className="h-px w-9 bg-gold-400/40" />
            </p>
            <h2 className="editorial-h2-section mt-3 !text-cream-50">
              {lang === 'en'
                ? 'We write it because we live it'
                : lang === 'es'
                ? 'Lo escribimos porque lo vivimos'
                : "On l'écrit parce qu'on le vit"}
            </h2>
          </header>
          <div className="grid grid-cols-1 gap-7 md:grid-cols-3">
            {PROOFS.map((p) => (
              <div key={p.key} className="border-t-2 border-gold-500 pt-4">
                <h3 className="font-serif text-lg font-bold italic text-cream-50">
                  {p.title[lang] || p.title.fr}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-cream-50/80">
                  {p.body[lang] || p.body.fr}
                </p>
              </div>
            ))}
          </div>
        </Container>
      </RevealOnScroll>

      {/* ============================================================
          V4 GLANCE — 4 stats éditoriales sous le hero
          (2026-05-26 Identité Visuelle V4 §02 SOMMAIRE VISUEL).
          Affichées en cream-50 avec hairlines sémantiques navy/10.
          ============================================================ */}
      <Container className="pt-10 md:pt-14">
        {/* Révélation animée du logo officiel — joue une fois à l'entrée
            dans le viewport puis se fige (consigne client : pas de boucle).
            Fond papier de la vidéo = cream-50 → fusion invisible.
            Fallback réserve la place exacte (aspect-square) → zéro CLS. */}
        <Suspense
          fallback={
            <div aria-hidden="true" className="mb-10 flex justify-center md:mb-12">
              <div className="aspect-square w-52 md:w-64" />
            </div>
          }
        >
          <LogoIntro className="mb-10 md:mb-12" size="lg" />
        </Suspense>
        <div className="v4-glance">
          <div className="v4-glance__cell">
            <div className="v4-glance__n">{totalArticles}<span className="v4-glance__u">{GLANCE.articles.unit[lang]}</span></div>
            <div className="v4-glance__k">{GLANCE.articles.k[lang]}</div>
          </div>
          <div className="v4-glance__cell">
            <div className="v4-glance__n">5<span className="v4-glance__u">{GLANCE.personas.unit[lang]}</span></div>
            <div className="v4-glance__k">{GLANCE.personas.k[lang]}</div>
          </div>
          <div className="v4-glance__cell">
            {/* Count dérivé du catalogue visible /shop (l'ancien « 27 » était périmé). */}
            <div className="v4-glance__n">{VISIBLE_PRODUCTS.length}<span className="v4-glance__u">{GLANCE.memos.unit[lang]}</span></div>
            <div className="v4-glance__k">{GLANCE.memos.k[lang]}</div>
          </div>
          <div className="v4-glance__cell">
            <div className="v4-glance__n">2026<span className="v4-glance__u">{GLANCE.issue.unit[lang]}</span></div>
            <div className="v4-glance__k">{GLANCE.issue.k[lang]}</div>
          </div>
        </div>
      </Container>

      {/* Continue reading ribbon — only renders if user has tracked progress */}
      <Container className="pt-6">
        <ContinueReading />
      </Container>

      {/* ============================================================
          2. PANAMA EN VIDÉO — Cinta Costera gallery (Jade footage)
          ============================================================ */}
      <RevealOnScroll as="section">
        <Container id="home-02-video" className="py-12 md:py-16">
          <header className="mb-6 max-w-2xl">
            <p className="v4-eyebrow mb-3 flex items-center gap-3 text-bordeaux-700 dark:text-bordeaux-400">
              <span className="h-px w-9 bg-bordeaux-600/40" />
              {lang === 'en' ? 'Panama on video' : lang === 'es' ? 'Panamá en video' : 'Panama en vidéo'}
            </p>
            <h2 className="editorial-h2-section dark:text-white">
              {lang === 'en'
                ? 'Cinta Costera at dusk — filmed by Jade'
                : lang === 'es'
                ? 'Cinta Costera al atardecer — filmado por Jade'
                : 'Cinta Costera au crépuscule — filmé par Jade'}
            </h2>
            <p className="mt-3 font-serif text-base italic leading-relaxed text-gray-700 dark:text-white/70">
              {lang === 'en'
                ? 'Three slice-of-life clips from Cinta Costera, the seafront promenade where most expats end up jogging or strolling.'
                : lang === 'es'
                ? 'Tres clips de vida cotidiana de la Cinta Costera, el paseo marítimo donde la mayoría de expats terminan corriendo o paseando.'
                : 'Trois clips slice-of-life depuis la Cinta Costera, le front de mer où la plupart des expats finissent par courir ou se promener.'}
            </p>
          </header>

          <div className="grid gap-4 sm:grid-cols-2 md:gap-6 lg:grid-cols-3">
            {[
              {
                src: '/videos/cinta-costera-sports.mp4',
                title: lang === 'en' ? 'Sports field & marina — afternoon'
                  : lang === 'es' ? 'Cancha deportiva y marina — tarde'
                  : 'Terrain de sport & marina — après-midi',
                caption: lang === 'en' ? 'The Cinta Costera connects Casco Antiguo to Punta Pacífica. Joggers, vendors, kids playing — it\'s the heartbeat of Panama City evenings.'
                  : lang === 'es' ? 'La Cinta Costera conecta el Casco Antiguo con Punta Pacífica. Joggers, vendedores, niños jugando — es el latido de las tardes de Panamá.'
                  : 'La Cinta Costera relie Casco Antiguo à Punta Pacífica. Joggers, vendeurs, enfants qui jouent — c\'est le rythme des soirées de Panama City.',
              },
              {
                src: '/videos/cinta-costera-balboa.mp4',
                title: lang === 'en' ? 'Vasco Núñez de Balboa statue'
                  : lang === 'es' ? 'Estatua de Vasco Núñez de Balboa'
                  : 'Statue de Vasco Núñez de Balboa',
                caption: lang === 'en' ? 'Honoring the European who first saw the Pacific from these shores in 1513. The monument sits on a roundabout halfway along the promenade.'
                  : lang === 'es' ? 'Homenaje al europeo que vio por primera vez el Pacífico desde estas costas en 1513. El monumento está en una glorieta a la mitad del paseo.'
                  : 'En hommage au premier Européen à voir le Pacifique depuis ces côtes en 1513. Le monument trône sur un rond-point à mi-promenade.',
              },
              {
                src: '/videos/cinta-costera-sunset.mp4',
                title: lang === 'en' ? 'Sunset & Cangrejo flower display'
                  : lang === 'es' ? 'Atardecer y decoración floral del Cangrejo'
                  : 'Coucher de soleil & déco florale du Cangrejo',
                caption: lang === 'en' ? 'The crab silhouette in front of a palm tree — Panama City\'s nod to neighborhood pride. Sunset filtering through dramatic tropical clouds.'
                  : lang === 'es' ? 'La silueta de cangrejo frente a una palmera — el guiño de Panamá al orgullo de barrio. Atardecer filtrándose por nubes tropicales dramáticas.'
                  : 'La silhouette du crabe devant un palmier — clin d\'œil de Panama City à la fierté de quartier. Coucher de soleil filtré par des nuages tropicaux.',
              },
            ].map((v) => (
              <figure key={v.src} className="overflow-hidden rounded-card border border-navy-900/10 bg-white shadow-card dark:border-white/10 dark:bg-navy-800/40">
                <video
                  src={v.src}
                  controls
                  preload="none"
                  playsInline
                  className="aspect-video w-full bg-navy-900 object-cover"
                  aria-label={v.title}
                >
                  {lang === 'en' ? 'Your browser does not support the video tag.' : lang === 'es' ? 'Tu navegador no soporta el video.' : 'Ton navigateur ne supporte pas la vidéo.'}
                </video>
                <figcaption className="p-4">
                  <p className="font-serif text-base font-semibold italic text-navy-900 dark:text-white">{v.title}</p>
                  <p className="mt-2 text-sm leading-relaxed text-gray-700 dark:text-white/70">{v.caption}</p>
                </figcaption>
              </figure>
            ))}
          </div>

          <p className="mt-4 text-xs italic text-gray-700 dark:text-white/60">
            {lang === 'en'
              ? 'Filmed on iPhone by Jade · May 2026 · Inside Panama'
              : lang === 'es'
              ? 'Filmado en iPhone por Jade · Mayo 2026 · Inside Panama'
              : 'Filmé sur iPhone par Jade · Mai 2026 · Inside Panama'}
          </p>
        </Container>
      </RevealOnScroll>

      {/* (Bande drapeaux FlagBand retirée 2026-06 — demande Jade.) */}

      {/* ============================================================
          3. BROWSE BY TOPIC — 8-cat grid (grid-cols-2 mobile pour lisibilité)
          ============================================================ */}
      <RevealOnScroll as="section">
      <Container id="home-03-topics" className="py-10 md:py-12">
        <p className="v4-eyebrow mb-4 flex items-center gap-3 text-bordeaux-700 dark:text-bordeaux-400">
          <span className="h-px w-9 bg-bordeaux-600/40" />
          {lang === 'en' ? 'Browse by topic' : lang === 'es' ? 'Explorar por tema' : 'Explorer par sujet'}
        </p>
        {/* Mobile : 2 colonnes (audit I·04 — pictos illisibles à 4 cols 360px).
            Tablet : 4. Desktop : 8. */}
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-8">
          {categories.map((cat) => {
            const Icon = CATEGORY_ICONS[cat.slug] || MapPin;
            return (
              <Link
                key={cat.slug}
                to={pathFor('category', lang, { slug: cat.slug })}
                className="card card-hoverable flex flex-col items-center gap-2 p-4 text-center transition-colors hover:border-bordeaux-600/30"
              >
                <span className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-bordeaux-600/10 text-bordeaux-700">
                  <Icon size={22} aria-hidden="true" />
                </span>
                <span className="text-sm font-medium leading-tight text-navy-900 dark:text-white">
                  {cat.label[lang] ?? cat.label.fr}
                </span>
              </Link>
            );
          })}
        </div>
      </Container>
      </RevealOnScroll>

      {/* ============================================================
          3b. MOT PANAMÉEN DU JOUR — remonté du footer vers le corps de la
          home (retour feedback Natasha/Jade : « mettre plus haut, personne
          ira en bas alors que c'est une bonne catégorie »). Placé juste
          après « Explorer par sujet » : la langue est un sujet à part
          entière, et le widget renvoie vers la catégorie espagnol.
          ============================================================ */}
      <Container className="py-10 md:py-12">
        <div className="mx-auto max-w-3xl">
          <SpanishPhraseOfDay />
          <p className="mt-3 text-center text-sm text-navy-700/80 dark:text-white/60">
            <Link
              to={pathFor('category', lang, { slug: 'espagnol' })}
              className="font-semibold text-bordeaux-700 underline-offset-2 hover:underline dark:text-bordeaux-400"
            >
              {lang === 'en'
                ? 'Learn Panamanian Spanish →'
                : lang === 'es'
                ? 'Aprende el español panameño →'
                : "Apprendre l'espagnol panaméen →"}
            </Link>
          </p>
        </div>
      </Container>

      {/* (Ancienne section « Par où commencer » retirée 2026-06 — remplacée
          par le ROUTER « Où en es-tu ? » promu juste après le hero.) */}

      {/* ============================================================
          5. ARTICLES ESSENTIELS — 6 piliers (RevealOnScroll conservé)
          ============================================================ */}
      {pillarArticles.length > 0 && (
        <RevealOnScroll as="section" id="home-05-essentials" className="bg-cream-100/60 py-16 md:py-20 dark:bg-white/[0.02]">
          <Container>
            <header className="mb-8 flex flex-wrap items-end justify-between gap-4">
              <div className="max-w-xl">
                <p className="eyebrow flex items-center gap-3 text-bordeaux-700 dark:text-bordeaux-400">
                  <span className="h-px w-9 bg-bordeaux-600/40" />
                  {t('pages:home.essentialsEyebrow', { defaultValue: 'Articles essentiels' })}
                </p>
                <h2 className="editorial-h2-headline mt-3 dark:text-white">
                  {t('pages:home.essentials')}
                </h2>
              </div>
              <Link
                to={pathFor('blog', lang)}
                className="inline-flex items-center gap-1.5 text-sm font-semibold text-bordeaux-700 hover:text-bordeaux-800 dark:text-bordeaux-400"
              >
                {t('common:viewAll')}
                <ArrowRight size={14} aria-hidden="true" />
              </Link>
            </header>
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {pillarArticles.map(({ frontmatter }) => (
                <ExpandableArticleCard
                  key={frontmatter.slug}
                  article={{ ...frontmatter, image: frontmatter.featuredImage }}
                  lang={lang}
                />
              ))}
            </div>
          </Container>
        </RevealOnScroll>
      )}

      {/* ============================================================
          6. OUTILS GRATUITS — 4 tuiles vers ancres dédiées + BudgetCalculator
          (audit C·03 : 4 tuiles → 4 vraies destinations, pas 4× /outils)
          ============================================================ */}
      <RevealOnScroll as="section">
      <Container id="home-06-tools" className="py-16 md:py-20">
        <header className="mb-8 max-w-2xl">
          <p className="eyebrow flex items-center gap-3 text-bordeaux-700 dark:text-bordeaux-400">
            <span className="h-px w-9 bg-bordeaux-600/40" />
            {t('pages:home.toolsEyebrow', { defaultValue: 'Outils gratuits' })}
          </p>
          <h2 className="editorial-h2-section mt-3 dark:text-white">
            {t('pages:home.tools')}
          </h2>
          <p className="mt-3 font-serif text-lg italic leading-relaxed text-navy-700/80 dark:text-white/70">
            {t('pages:home.toolsLede', {
              defaultValue: 'Quatre calculatrices pour dimensionner ton projet sans inscription.',
            })}
          </p>
        </header>
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          {TOOL_TILES.map(({ icon: Icon, key, route }) => (
            <Tilt key={key} className="h-full">
            <Link
              to={pathFor(route, lang)}
              className="card card-hoverable flex h-full flex-col items-start gap-3 p-5 transition hover:-translate-y-0.5 hover:border-tool-600/30"
            >
              <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-tool-100 text-tool-700 dark:bg-tool-800 dark:text-tool-300">
                <Icon size={18} aria-hidden="true" />
              </span>
              <p className="font-medium text-navy-900 dark:text-white">{t(`pages:tools.miniCards.${key}`)}</p>
            </Link>
            </Tilt>
          ))}
        </div>
        <div className="mt-10">
          <Suspense fallback={<LoadingSpinner />}>
            <BudgetCalculator />
          </Suspense>
        </div>
      </Container>
      </RevealOnScroll>

      {/* ============================================================
          6a. COURS GRATUIT — lead magnet « Move in 60 Days »
          Bandeau (PAS une capture : renvoie vers la landing dédiée →
          respecte l'audit C·02 « 2 surfaces de capture »).
          2026-07-21 — remonté d'après la galerie (était en 7.5) : le gratuit
          passait APRÈS l'échelle payante, ce que le commentaire 6b démentait.
          ============================================================ */}
      <RevealOnScroll as="section">
        <Container className="py-16 md:py-20">
          <div className="relative overflow-hidden rounded-card border border-bordeaux-600/20 bg-bordeaux-600/[0.04] p-8 dark:border-bordeaux-400/25 dark:bg-bordeaux-400/[0.06] md:p-12">
            <div className="grid items-center gap-8 md:grid-cols-[1.4fr_auto]">
              <div>
                <p className="v4-label-mono text-bordeaux-700 dark:text-bordeaux-400">
                  {lang === 'en' ? 'Free email course · 9 steps' : lang === 'es' ? 'Curso por email gratis · 9 pasos' : 'Cours email gratuit · 9 étapes'}
                </p>
                <h2 className="mt-3 font-serif text-3xl font-bold text-navy-900 dark:text-white md:text-4xl">
                  {lang === 'en' ? 'Move to Panama in 60 days' : lang === 'es' ? 'Múdate a Panamá en 60 días' : 'Installe-toi au Panama en 60 jours'}
                </h2>
                <p className="mt-3 max-w-xl text-pretty leading-relaxed text-gray-700 dark:text-white/75">
                  {lang === 'en'
                    ? 'One email per step, from the visa to your first night in Panama City. The real path, written by people who did it.'
                    : lang === 'es'
                      ? 'Un email por paso, desde la visa hasta tu primera noche en Ciudad de Panamá. El recorrido real, escrito por quienes lo hicieron.'
                      : "Un email à chaque étape, du visa à ta première nuit à Panama City. Le parcours réel, écrit par celles qui l'ont fait."}
                </p>
              </div>
              <Link
                to={pathFor('move60', lang)}
                className="btn-primary shrink-0 justify-self-start md:justify-self-end"
              >
                {lang === 'en' ? 'Start the 60 days' : lang === 'es' ? 'Empezar los 60 días' : 'Commencer les 60 jours'}
                <ArrowRight size={16} aria-hidden="true" />
              </Link>
            </div>
          </div>
        </Container>
      </RevealOnScroll>

      {/* ============================================================
          6b. BOUTIQUE — l'échelle de valeur en 4 paliers (T1, brief zone 5)
          Enchaîne après les outils gratuits : free → ladder payante. → /shop.
          ============================================================ */}
      <RevealOnScroll as="section">
        <Container id="home-shop-ladder" className="py-16 md:py-20">
          <header className="mb-8 max-w-2xl">
            <p className="eyebrow flex items-center gap-3 text-bordeaux-700 dark:text-bordeaux-400">
              <span className="h-px w-9 bg-bordeaux-600/40" />
              {lang === 'en' ? 'The shop' : lang === 'es' ? 'La tienda' : 'La boutique'}
            </p>
            <h2 className="editorial-h2-section mt-3 dark:text-white">
              {lang === 'en' ? 'A tier for every stage' : lang === 'es' ? 'Un nivel para cada etapa' : 'Un palier pour chaque étape'}
            </h2>
            <p className="mt-3 font-serif text-lg italic leading-relaxed text-navy-700/80 dark:text-white/70">
              {lang === 'en'
                ? 'From the first free landmark to full guidance — pick by how committed you are.'
                : lang === 'es'
                ? 'Del primer punto de referencia gratuito al acompañamiento completo — elige según tu compromiso.'
                : "Du premier repère gratuit à l'accompagnement complet — choisis selon ton niveau d'engagement."}
            </p>
          </header>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {HOME_TIERS.map((tr) => (
              <Link
                key={tr.key}
                to={`${pathFor('shop', lang)}#guides`}
                className={`card card-hoverable group flex flex-col border p-5 transition hover:-translate-y-0.5 hover:shadow-lg ${tr.ring}`}
              >
                <div className="flex items-baseline justify-between gap-2">
                  <span className={`v4-eyebrow ${tr.eyebrow}`}>{t(`pages:shop.tier.${tr.key}`)}</span>
                  {tr.seal && <WaveMark width={26} />}
                </div>
                <span className="mt-2 font-mono text-2xl font-bold tabular-nums text-navy-900 dark:text-white">{tr.band}</span>
                <p className="mt-2 flex-1 text-sm leading-relaxed text-gray-700 dark:text-white/70">{tr.blurb[lang] || tr.blurb.fr}</p>
                <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-bordeaux-700 dark:text-bordeaux-400">
                  {lang === 'en' ? 'Explore' : lang === 'es' ? 'Explorar' : 'Explorer'}
                  <ArrowRight size={14} aria-hidden="true" className="transition-transform group-hover:translate-x-0.5" />
                </span>
              </Link>
            ))}
          </div>
        </Container>
      </RevealOnScroll>

      {/* ============================================================
          7. PANAMA PLACES GALLERY — texture éditoriale
          ============================================================ */}
      <Suspense fallback={<LoadingSpinner />}>
        <PanamaPlacesGallery />
      </Suspense>

      {/* ============================================================
          8. NEWSLETTER BANNER — la SEULE capture email en home
          (audit C·02 : 5 surfaces → 2 — celle-ci + page /newsletter)
          ============================================================ */}
      <RevealOnScroll as="div"><Container className="py-16 md:py-20">
        <NewsletterBanner leadMagnet="home-banner" />
      </Container></RevealOnScroll>

      {/* ============================================================
          9. DERNIERS ARTICLES — recency anchor
          ============================================================ */}
      {latestArticles.length > 0 && (
        <RevealOnScroll as="section">
        <Container id="home-09-latest" className="py-16 md:py-20">
          <header className="mb-8 flex flex-wrap items-end justify-between gap-4">
            <div className="max-w-xl">
              <p className="eyebrow flex items-center gap-3 text-bordeaux-700 dark:text-bordeaux-400">
                <span className="h-px w-9 bg-bordeaux-600/40" />
                {t('pages:home.latestEyebrow', { defaultValue: 'Le plus récent' })}
              </p>
              <h2 className="editorial-h2-section mt-3 dark:text-white">
                {t('pages:home.latest')}
              </h2>
            </div>
            <Link
              to={pathFor('blog', lang)}
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-bordeaux-700 hover:text-bordeaux-800 dark:text-bordeaux-400"
            >
              {t('common:viewAll')}
              <ArrowRight size={14} aria-hidden="true" />
            </Link>
          </header>
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {latestArticles.map(({ frontmatter }) => (
              <ArticleCard key={frontmatter.slug} article={frontmatter} lang={lang} />
            ))}
          </div>
        </Container>
        </RevealOnScroll>
      )}

      {/* ============================================================
          10. QUIZ PERSONA — engagement tail pour visiteurs en exploration
          ============================================================ */}
      <section className="bg-cream-100/60 py-16 md:py-20 dark:bg-white/[0.02]">
        <Container>
          <Suspense fallback={<LoadingSpinner />}>
            <ExpatPersonaQuiz />
          </Suspense>
        </Container>
      </section>
    </>
  );
}
